// @flow
import React from "react";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";

type $Props = {
  classes: Object,
};

const Mistakes = ({ classes }: $Props) => (
  <div className={classes.root}>
    <Typography type="body">
      Here we lay out the common mistakes home sellers make, do your best to
      avoid these and you'll be on your way to home selling bliss.{" "}
    </Typography>

    <img
      className={classes.inlineImage}
      src="https://storage.googleapis.com/elevate-images/elevate-tips-mistakes.jpg"
      align="right"
      alt=""
    />

    <Typography className={classes.subHeading} type="heading4">
      Basing the asking price on needs or emotion rather than market value
    </Typography>
    <Typography type="body">
      Many times sellers base their pricing on how much they paid for or
      invested in their home. This can be an expensive mistake. If your home is
      not priced competitively, buyers will reject it in favor of other larger
      homes for the same price. At the same time, the buyers who should be
      looking at your house will not see it because it is priced over their
      heads. The result is increased market time, and even when the price is
      eventually lowered, the buyers are wary because "nobody wants to buy real
      estate that nobody else wants". The result is low priced offers and an
      unwillingness to negotiate. Every seller wants to realize as much money as
      possible from the sale, but a listing priced too high often eventually
      sells for less than market value. An accurate{" "}
      <a href="/sell">market evaluation</a> is the first step in determining a
      competitive listing price.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Failing to "Showcase" the home
    </Typography>
    <Typography type="body">
      A property that is not clean or well maintained is a red flag for the
      buyer. It is an indication that there may be hidden defects that will
      result in increased cost of ownership. Sellers who fail to make necessary
      repairs, who don't “spruce up” the house inside and out, and fail to keep
      it clean and neat, chase away buyers as fast as REALTORS® can bring them.
      Buyers are poor judges of the cost of repairs, and always build in a large
      margin for error when offering on such a property. Sellers are always
      better off doing the work themselves ahead of time.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Over-improving the home prior to selling
    </Typography>
    <Typography type="body">
      Sellers often unwittingly spend thousands of dollars doing the wrong
      upgrades to their home prior to attempting to sell in the mistaken belief
      that they will recoup this cost. If you are upgrading your home for your
      personal enjoyment - fine. But if you are thinking of selling, you should
      be aware that only certain upgrades to real estate are cost effective.
      Always consult with your REALTOR® BEFORE committing to upgrading your
      home.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Choosing the wrong REALTOR® or choosing for the wrong reasons
    </Typography>
    <Typography type="body">
      Many homeowners list with the real estate agent who tells them the highest
      price. You need to choose an experienced agent with the best marketing
      plan to sell your home. In the real estate business, an agent with many
      successfully closed transactions usually costs the same as someone who is
      inexperienced. That experience could mean a higher price at the
      negotiating table, selling in less time, and with a minimum amount of
      hassles.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Using the "Hard Sell" during showings
    </Typography>
    <Typography type="body">
      Buying a home is an emotional decision. Buyers like to "try on" a house
      and see if it is comfortable for them. It is difficult for them to do if
      you follow them around pointing out every improvement that you made. Good
      REALTORS® let the buyers discover the home on their own, pointing out only
      features they are sure are important to them. Overselling loses many
      sales. If buyers think they are paying for features that are not
      particularly important to them personally, they will reject the home in
      favor of a less expensive home without the features.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Failing to take the first offer seriously
    </Typography>
    <Typography type="body">
      Often sellers believe that the first offer received will be one of many to
      come. There is a tendency to not take it seriously, and to hold out for a
      higher price. This is especially true if the offer comes in soon after the
      home is placed on the market. Experienced REALTORS® know that more often
      than not the first buyer ends up being the best buyer, and many, many
      sellers have had to accept far less money than the initial offer later in
      the selling process. Real estate is most saleable early in the marketing
      period, and the amount buyers are willing to pay diminishes with the
      length of time a property has been on the market. Many sellers would give
      anything to find that prospective buyer who made the first, and ONLY,
      offer.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Not knowing your rights and obligations
    </Typography>
    <Typography type="body">
      The contract you sign to sell your property is a complex and legally
      binding document. An improperly written contract can allow the purchaser
      to void the sale, or cost you thousands of unnecessary dollars. Have an
      experienced REALTOR® who knows the "ins and outs" fully explain the
      contract you are about to sign.
    </Typography>
    <Typography className={classes.subHeading} type="heading4">
      Failure to effectively market the property
    </Typography>
    <Typography type="body">
      Good marketing opens the door that exposes real estate to the marketplace.
      It means distinguishing your home from hundreds of others on the market.
      It also means selling the benefits, as well as the features. The right
      REALTOR® will employ a wide variety of marketing activities, emphasizing
      the ones believed to work best for your home.
    </Typography>
  </div>
);

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "20px auto",
    maxWidth: "660px",

    "& strong": {
      fontWeight: "bold",
    },

    "& p": {
      color: theme.colors.gray["700"],
      lineHeight: "1.6",
      marginBottom: theme.spacing.unit * 1.5,
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },
    },

    "& ul": {
      listStyleType: "disc",
      lineHeight: "1.6",
      color: theme.colors.gray["700"],
      margin: "20px 14px 20px 40px",
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },

      "& li": {
        marginBottom: theme.spacing.unit * 1.5,
      },
    },
  },
  subHeading: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 0.5,
    fontSize: "21px",
    lineHeight: "1.3",

    [theme.breakpoints(800)]: {
      fontSize: "24px",
    },
  },
  inlineImage: {
    width: "100%",
    marginBottom: theme.spacing.unit * 1.5,
    display: "block",
    maxWidth: "100%",
    height: "auto",
    verticalAlign: "middle",
  },
});

export default withStyles(styles)(Mistakes);
